/**
 * CommentsTab Component
 *
 * Communication log tab for investments - allows all users to comment and reply.
 * Displays comments in descending order by date with threaded replies.
 */

import {
  useInvestmentComments,
  useAddInvestmentComment,
  useDeleteInvestmentComment,
} from '@medsuite/api-client';
import { Button, TextArea, Card, Spinner, Alert, useDialog } from '@medsuite/ui-components';
import React, { useState } from 'react';

import { useAuth } from '../../../providers/AuthProvider';

import type { Investment, InvestmentComment } from '@medsuite/models';

interface CommentsTabProps {
  investment: Investment;
}

/**
 * Format date for display
 */
const formatDate = (date: Date | string) => {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Get initials from name
 */
const getInitials = (name: string) => {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Comment display component
 */
const CommentItem: React.FC<{
  comment: InvestmentComment;
  currentUserEmail?: string;
  onReply: (parentId: number) => void;
  onDelete: (commentId: number) => void;
  isDeleting: boolean;
  depth?: number;
}> = ({ comment, currentUserEmail, onReply, onDelete, isDeleting, depth = 0 }) => {
  const isOwner = currentUserEmail && comment.comment_by_email === currentUserEmail;
  const maxDepth = 2; // Maximum nesting level

  return (
    <div
      className={`${depth > 0 ? 'ml-8 border-l-2 border-gray-200 dark:border-gray-700 pl-4' : ''}`}
    >
      <div className="flex gap-3 py-4">
        {/* Avatar */}
        <div className="flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center text-indigo-600 dark:text-indigo-300 font-semibold text-sm">
            {getInitials(comment.comment_by_name)}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {comment.comment_by_name}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {comment.comment_by_email}
            </span>
            <span className="text-sm text-gray-400 dark:text-gray-500">
              {formatDate(comment.comment_date)}
            </span>
          </div>

          <div className="mt-1 text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
            {comment.comment}
          </div>

          {/* Actions */}
          <div className="mt-2 flex gap-4">
            {depth < maxDepth && (
              <button
                type="button"
                onClick={() => onReply(comment.investment_comment_id)}
                className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
              >
                Reply
              </button>
            )}
            {isOwner && (
              <button
                type="button"
                onClick={() => onDelete(comment.investment_comment_id)}
                disabled={isDeleting}
                className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * CommentsTab Component
 */
export const CommentsTab: React.FC<CommentsTabProps> = ({ investment }) => {
  // Get current user from auth context
  const { user } = useAuth();
  const currentUserEmail = user?.username || 'unknown@example.com';
  const currentUserName = user?.name || 'Unknown User';
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyText, setReplyText] = useState('');

  // Fetch comments
  const {
    data: comments = [],
    isLoading,
    isError,
    error,
  } = useInvestmentComments(investment.investment_id);

  // Mutations
  const addComment = useAddInvestmentComment(investment.investment_id);
  const deleteComment = useDeleteInvestmentComment(investment.investment_id);

  // Dialog hooks
  const { confirm } = useDialog();

  /**
   * Handle submitting a new top-level comment
   */
  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) {
      return;
    }

    try {
      await addComment.mutateAsync({
        comment: newComment.trim(),
        comment_by_email: currentUserEmail,
        comment_by_name: currentUserName,
      });
      setNewComment('');
    } catch (err) {
      console.error('Failed to add comment:', err);
    }
  };

  /**
   * Handle submitting a reply
   */
  const handleSubmitReply = async (parentId: number) => {
    if (!replyText.trim()) {
      return;
    }

    try {
      await addComment.mutateAsync({
        comment: replyText.trim(),
        comment_by_email: currentUserEmail,
        comment_by_name: currentUserName,
        parent_comment_id: parentId,
      });
      setReplyText('');
      setReplyingTo(null);
    } catch (err) {
      console.error('Failed to add reply:', err);
    }
  };

  /**
   * Handle deleting a comment
   */
  const handleDelete = async (commentId: number) => {
    const confirmed = await confirm({
      title: 'Delete Comment',
      message: 'Are you sure you want to delete this comment?',
      variant: 'danger',
      confirmText: 'Delete',
    });
    if (!confirmed) {
      return;
    }

    try {
      await deleteComment.mutateAsync(commentId);
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  /**
   * Build threaded comment structure
   */
  const buildThreadedComments = (comments: InvestmentComment[]) => {
    const commentMap = new Map<number, InvestmentComment & { replies: InvestmentComment[] }>();
    const topLevel: (InvestmentComment & { replies: InvestmentComment[] })[] = [];

    // First pass: create map with empty replies
    for (const comment of comments) {
      commentMap.set(comment.investment_comment_id, { ...comment, replies: [] });
    }

    // Second pass: build tree
    for (const comment of comments) {
      const commentWithReplies = commentMap.get(comment.investment_comment_id)!;
      if (comment.parent_comment_id) {
        const parent = commentMap.get(comment.parent_comment_id);
        if (parent) {
          parent.replies.push(commentWithReplies);
        }
      } else {
        topLevel.push(commentWithReplies);
      }
    }

    // Sort top-level by date desc, replies by date asc
    topLevel.sort(
      (a, b) => new Date(b.comment_date).getTime() - new Date(a.comment_date).getTime()
    );

    const sortReplies = (comments: (InvestmentComment & { replies: InvestmentComment[] })[]) => {
      for (const comment of comments) {
        comment.replies.sort(
          (a, b) => new Date(a.comment_date).getTime() - new Date(b.comment_date).getTime()
        );
        sortReplies(comment.replies as (InvestmentComment & { replies: InvestmentComment[] })[]);
      }
    };
    sortReplies(topLevel);

    return topLevel;
  };

  /**
   * Render a comment and its replies recursively
   */
  const renderComment = (
    comment: InvestmentComment & { replies?: InvestmentComment[] },
    depth = 0
  ): React.ReactNode => {
    return (
      <div key={comment.investment_comment_id}>
        <CommentItem
          comment={comment}
          currentUserEmail={currentUserEmail}
          onReply={(parentId) => {
            setReplyingTo(parentId);
            setReplyText('');
          }}
          onDelete={handleDelete}
          isDeleting={deleteComment.isPending}
          depth={depth}
        />

        {/* Reply form */}
        {replyingTo === comment.investment_comment_id && (
          <div className="ml-12 mb-4">
            <div className="flex gap-2">
              <TextArea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Write a reply..."
                rows={2}
                className="flex-1"
              />
            </div>
            <div className="mt-2 flex gap-2">
              <Button
                type="button"
                size="sm"
                onClick={() => handleSubmitReply(comment.investment_comment_id)}
                disabled={!replyText.trim() || addComment.isPending}
              >
                {addComment.isPending ? 'Posting...' : 'Reply'}
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setReplyingTo(null)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Nested replies */}
        {comment.replies && comment.replies.length > 0 && (
          <div>{comment.replies.map((reply) => renderComment(reply as InvestmentComment & { replies?: InvestmentComment[] }, depth + 1))}</div>
        )}
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading comments...</span>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <Alert variant="error">
        <p className="font-semibold">Failed to load comments</p>
        <p className="text-sm">{(error as Error)?.message || 'Unknown error'}</p>
      </Alert>
    );
  }

  const threadedComments = buildThreadedComments(comments);

  return (
    <div className="space-y-6">
      {/* New Comment Form */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Communication Log
          </h3>

          <form onSubmit={handleSubmitComment}>
            <TextArea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              rows={3}
            />
            <div className="mt-3 flex justify-end">
              <Button type="submit" disabled={!newComment.trim() || addComment.isPending}>
                {addComment.isPending ? 'Posting...' : 'Post Comment'}
              </Button>
            </div>
          </form>
        </div>
      </Card>

      {/* Comments List */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Comments ({comments.length})
          </h3>

          {threadedComments.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No comments yet. Be the first to add a comment!
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {threadedComments.map((comment) => renderComment(comment))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default CommentsTab;
